import { collection, addDoc, getDocs, doc, updateDoc, getDoc, setDoc, Timestamp, query, orderBy } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { WaitlistData } from '@/types/waitlist';

type WaitlistSubmissionInput = {
  fullName: string;
  email: string;
  instagram: string;
  pricingTrack?: 'live' | 'vod';
  livePlanId?: string | null;
  vodBundle?: boolean | null;
  vodPlanId?: string | null;
  flowVersion?: string;
};

function buildWaitlistPayload(data: WaitlistSubmissionInput): Record<string, unknown> {
  const waitlistData: Record<string, unknown> = {
    fullName: data.fullName,
    email: data.email,
    instagramLink: normalizeInstagramLink(data.instagram),
    createdAt: Timestamp.now(),
    invited: 0,
  };

  if (data.pricingTrack) waitlistData.pricingTrack = data.pricingTrack;
  if (data.livePlanId != null) waitlistData.livePlanId = data.livePlanId;
  if (data.vodBundle != null) waitlistData.vodBundle = data.vodBundle;
  if (data.vodPlanId != null) waitlistData.vodPlanId = data.vodPlanId;
  if (data.flowVersion) waitlistData.flowVersion = data.flowVersion;

  return waitlistData;
}

/**
 * Helper function to normalize Instagram input
 * Accepts either a handle (@username or username) or a full URL
 */
export function normalizeInstagramLink(input: string): string {
  const trimmed = input.trim();
  
  // If it's already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  
  // If it starts with instagram.com
  if (trimmed.startsWith('instagram.com/')) {
    return `https://${trimmed}`;
  }
  
  // Remove @ if present and create URL
  const username = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
  return `https://www.instagram.com/${username}`;
}

/**
 * Create a new waitlist submission
 */
export async function createWaitlistSubmission(data: WaitlistSubmissionInput): Promise<string> {
  const docRef = await addDoc(collection(db, 'waitlist'), buildWaitlistPayload(data));
  return docRef.id;
}

const WRITE_TIMEOUT_MS = 10_000;
const WRITE_ATTEMPTS = 3;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('waitlist_write_timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function firestoreErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

/**
 * Public waitlist rules allow create but not update or read (`firestore.rules`
 * `match /waitlist/{waitlistId}`). A timed-out `setDoc` can still commit; the
 * retry then becomes an update and is denied. That means the doc already exists.
 */
function isAlreadyCreatedError(error: unknown): boolean {
  const code = firestoreErrorCode(error);
  if (code === 'permission-denied' || code === 'already-exists') return true;
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /permission[- ]denied|already.?exists|insufficient permissions/i.test(message);
}

/**
 * Waitlist write after an offline/online toggle can hang or reject once.
 * One client-generated id is reused on every attempt (`setDoc`), so a timed-out
 * first write cannot create a second document when it later commits.
 */
export async function createWaitlistSubmissionResilient(
  data: WaitlistSubmissionInput
): Promise<string> {
  const docRef = doc(collection(db, 'waitlist'));
  let lastError: unknown;
  for (let attempt = 0; attempt < WRITE_ATTEMPTS; attempt += 1) {
    try {
      await withTimeout(setDoc(docRef, buildWaitlistPayload(data)), WRITE_TIMEOUT_MS);
      return docRef.id;
    } catch (error) {
      if (attempt > 0 && isAlreadyCreatedError(error)) {
        return docRef.id;
      }
      lastError = error;
      if (attempt < WRITE_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

/**
 * Get all waitlist submissions (excluding soft-deleted ones)
 */
export async function getAllWaitlistSubmissions(): Promise<(WaitlistData & { id: string })[]> {
  try {
    const q = query(collection(db, 'waitlist'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs
      .map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          fullName: data.fullName,
          email: data.email,
          instagramLink: data.instagramLink,
          createdAt: data.createdAt?.toDate() || new Date(),
          invited: data.invited || 0,
          inviteId: data.inviteId,
          invitedAt: data.invitedAt?.toDate(),
          deleted: data.deleted || false,
          deletedAt: data.deletedAt?.toDate(),
          pricingTrack: data.pricingTrack,
          livePlanId: data.livePlanId ?? null,
          vodBundle: data.vodBundle ?? null,
          vodPlanId: data.vodPlanId ?? null,
          flowVersion: data.flowVersion,
        };
      })
      .filter((item) => !item.deleted); // Filter out soft-deleted items
  } catch (error) {
    console.error('Error fetching waitlist submissions:', error);
    return [];
  }
}

/**
 * Get pending waitlist submissions (invited = 0)
 */
export async function getPendingWaitlistSubmissions(): Promise<(WaitlistData & { id: string })[]> {
  const allSubmissions = await getAllWaitlistSubmissions();
  return allSubmissions.filter(submission => submission.invited === 0);
}

/**
 * Get invited waitlist submissions (invited = 1)
 */
export async function getInvitedWaitlistSubmissions(): Promise<(WaitlistData & { id: string })[]> {
  const allSubmissions = await getAllWaitlistSubmissions();
  return allSubmissions.filter(submission => submission.invited === 1);
}

/**
 * Mark a waitlist submission as invited
 */
export async function markWaitlistAsInvited(waitlistId: string, inviteId: string): Promise<void> {
  const waitlistRef = doc(db, 'waitlist', waitlistId);
  await updateDoc(waitlistRef, {
    invited: 1,
    inviteId: inviteId,
    invitedAt: Timestamp.now(),
  });
}

/**
 * Get a single waitlist submission by ID
 */
export async function getWaitlistSubmission(waitlistId: string): Promise<(WaitlistData & { id: string }) | null> {
  try {
    const docRef = doc(db, 'waitlist', waitlistId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        fullName: data.fullName,
        email: data.email,
        instagramLink: data.instagramLink,
        createdAt: data.createdAt?.toDate() || new Date(),
        invited: data.invited || 0,
        inviteId: data.inviteId,
        invitedAt: data.invitedAt?.toDate(),
        deleted: data.deleted || false,
        deletedAt: data.deletedAt?.toDate(),
        pricingTrack: data.pricingTrack,
        livePlanId: data.livePlanId ?? null,
        vodBundle: data.vodBundle ?? null,
        vodPlanId: data.vodPlanId ?? null,
        flowVersion: data.flowVersion,
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching waitlist submission:', error);
    return null;
  }
}

/**
 * Soft delete a waitlist submission
 * Sets deleted flag to true instead of removing from database
 */
export async function softDeleteWaitlistSubmission(waitlistId: string): Promise<void> {
  const waitlistRef = doc(db, 'waitlist', waitlistId);
  await updateDoc(waitlistRef, {
    deleted: true,
    deletedAt: Timestamp.now(),
  });
}

